import i18n from "i18next";
import { initReactI18next } from "react-i18next";
// NOTE: importing directly from the getClientUrl is required for the build to work on DEV (NO IDEA WHY)
import { getClientUrl } from "./shared/utils/getClientUrl";

/**
 * Capture the script's own origin at module evaluation time. Widget bundles are
 * ES modules loaded via `<script type="module" src="…/widget/operator/main.js">`.
 * `import.meta.url` gives the chunk URL at runtime, from which we extract the
 * origin. This lets us build absolute locale URLs even when `widgetType` isn't
 * explicitly passed to `loadLanguagePacks`.
 */
const _scriptOrigin = (() => {
  try {
    return new URL(import.meta.url).origin;
  } catch {
    // SSR or non-standard environment
  }
  return "";
})();

export enum i18nLanguage {
  en = "en",
  es = "es",
  fr = "fr",
}

export enum i18nNamespace {
  Core = "core",
  Home = "home",
  Directory = "directory",
  CustomLists = "customLists",
  Campaigns = "campaigns",
  Widgets = "widgets",
  Settings = "settings",
  Onboarding = "onboarding",
  PostOnboarding = "postOnboarding",
  Events = "events",
  Booker = "booker",
  Tripper = "tripper",
  Storefront = "storefront",
  Adminland = "adminland",
  People = "people",
  QueryBuilder = "querybuilder",
}

export type i18nWidgetType =
  | "event"
  | "event-submit"
  | "operator"
  | "booker"
  /** Same locale files as `booker`, but served from `/widget/bookerModal/` (embed iframe bundle). */
  | "bookerModal"
  | "tripper"
  | "whats-nearby"
  | "inquiry"
  | "gallery"
  | "media-profile"
  | "promotions"
  | "";

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    lng: i18nLanguage.en, // language to use, more information here: https://www.i18next.com/overview/configuration-options#languages-namespaces-resources
    // you can use the i18n.changeLanguage function to change the language manually: https://www.i18next.com/overview/api#changelanguage
    // if you're using a language detector, do not define the lng option

    interpolation: {
      escapeValue: false, // react already safes from xss
    },
  });

/**
 * Downloads and adds the new language/namespace to i18n
 * @param lng
 * @param ns
 * @returns
 */
const addLocaleAndNamespace = async (
  lng: i18nLanguage,
  ns: i18nNamespace,
  widgetType: i18nWidgetType,
) => {
  // If the bundle has already been added, no need to load it again
  if (i18n.getResourceBundle(lng, ns)) {
    return i18n;
  }

  /**
   * Widgets run on third-party sites, so relative URLs would fetch from the
   * wrong host. We always build an absolute URL when we detect the script is
   * running on a foreign origin (i.e. the page origin differs from the app's).
   *
   * For the main app (same origin), relative paths work and support deploy
   * previews. In Vite dev, `public/locales/` is served at root.
   **/
  let baseUrl = "";
  if (!import.meta.env.DEV) {
    if (widgetType) {
      const CLIENT_URL = getClientUrl();
      let widgetPath: string = widgetType;
      if (
        widgetType === "tripper" ||
        widgetType === "gallery" ||
        widgetType === "media-profile" ||
        widgetType === "promotions"
      ) {
        widgetPath = `${widgetType}/v1`;
      } else if (widgetType === "operator" && __LEGACY_OPERATORS_WIDGET__) {
        widgetPath = `${widgetType}/legacy`;
      }
      baseUrl = `${CLIENT_URL}/widget/${widgetPath}`;
    } else if (_scriptOrigin && _scriptOrigin !== window.location.origin) {
      // No widgetType was passed (e.g. a shared form hook loaded a namespace),
      // but we're on a third-party page — resolve relative to the script origin.
      baseUrl = _scriptOrigin;
    }
  }

  const url = `${baseUrl}/locales/${lng}/${ns}.json`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.warn(`[i18n] Failed to load ${ns} (${response.status}) from ${url}`);
      return i18n;
    }
    const resources = await response.json();
    return i18n.addResourceBundle(lng, ns, resources, false, true);
  } catch {
    return i18n;
  }
};

// Set the default language to "en"
i18n.changeLanguage(i18nLanguage.en);

export const loadLanguagePacks = async (
  namespaces: i18nNamespace[],
  widgetType: i18nWidgetType = "",
  i18Lang?: i18nLanguage,
) => {
  return Promise.all(
    namespaces.map(ns =>
      addLocaleAndNamespace(
        i18Lang ?? (i18n.language as i18nLanguage),
        ns,
        widgetType,
      ),
    ),
  );
};

export default i18n;
